export type ReconnectConfig = {
  initialDelayMs?: number
  maxDelayMs?: number
  backoffFactor?: number
  maxRetries?: number
}

type ReconnectCallbacks = {
  onReconnect: () => void
  onGiveUp: (lastError?: Error) => void
}

export type ReconnectScheduler = {
  scheduleReconnect: (lastError?: Error) => boolean
  notifySuccess: () => void
  notifyFinished: () => void
  dispose: () => void
}

const DEFAULTS = {
  initialDelayMs: 1_000,
  maxDelayMs: 30_000,
  backoffFactor: 2,
  maxRetries: 10,
} as const

export function calculateBackoffDelay(retryCount: number, config?: ReconnectConfig): number {
  const initialDelay = config?.initialDelayMs ?? DEFAULTS.initialDelayMs
  const maxDelay = config?.maxDelayMs ?? DEFAULTS.maxDelayMs
  const factor = config?.backoffFactor ?? DEFAULTS.backoffFactor

  const base = Math.min(initialDelay * factor ** retryCount, maxDelay)
  const jitter = base * (0.5 + Math.random() * 0.5)
  return jitter
}

export function createReconnectScheduler(callbacks: ReconnectCallbacks, config?: ReconnectConfig): ReconnectScheduler {
  const maxRetries = config?.maxRetries ?? DEFAULTS.maxRetries

  let retryCount = 0
  let finished = false
  let timer: ReturnType<typeof setTimeout> | null = null

  function scheduleReconnect(lastError?: Error): boolean {
    if (finished) return false

    retryCount++

    if (retryCount >= maxRetries) {
      callbacks.onGiveUp(lastError)
      return false
    }

    const delay = calculateBackoffDelay(retryCount, config)
    timer = setTimeout(() => {
      callbacks.onReconnect()
    }, delay)
    return true
  }

  function notifySuccess(): void {
    retryCount = 0
  }

  function notifyFinished(): void {
    finished = true
  }

  function dispose(): void {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  return { scheduleReconnect, notifySuccess, notifyFinished, dispose }
}
